import * as React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PlaceModel } from "../store/features/estimateSlice";
import { useDebounce } from "use-debounce";
import { InputLabel } from "@mui/material";

const places = (window as any).google.maps.places;
const placesService = new places.AutocompleteService();

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}

interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings: readonly MainTextMatchedSubstrings[];
}

interface PlaceType {
  description: string;
  place_id: string;
  structured_formatting: StructuredFormatting;
}

interface TownSelectProps {
  town?: PlaceModel;
  errors?: any;
  updateTown: (town: PlaceModel | undefined) => void;
}

export default function TownSelect(props: TownSelectProps) {
  const { town, updateTown, errors } = props;
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);
  const [options, setOptions] = useState<PlaceType[]>([]);

  useEffect(() => {
    let isActive = true;
    placesService.getPlacePredictions(
      {
        input: debouncedSearchTerm,
        componentRestrictions: {
          country: "au",
        },
        types: ["(cities)"],
      },
      (results: PlaceType[]) => {
        if (isActive && results) setOptions(results);
        else setOptions([]);
      }
    );

    return () => {
      isActive = false;
    };
  }, [debouncedSearchTerm]);

  const value = useMemo(() => {
    if (!town) return null;
    return {
      place_id: town.id,
      description: town.value,
      structured_formatting: {
        main_text: town.value,
        main_text_matched_substrings: [],
        secondary_text: "",
      },
    };
  }, [town]);

  const onChange = useCallback(
    (_, value: PlaceType | null) => {
      updateTown(
        value
          ? {
              id: value.place_id,
              value: value.structured_formatting.main_text,
            }
          : undefined
      );
    },
    [updateTown]
  );

  return (
    <>
      <InputLabel htmlFor="townName" required variant="standard" shrink>
        {t("start.town")}
      </InputLabel>
      <Autocomplete
        getOptionLabel={(option) => option.structured_formatting.main_text}
        isOptionEqualToValue={(option, value) =>
          option.place_id === value.place_id
        }
        filterOptions={(option) => option}
        options={options}
        autoComplete
        popupIcon={null}
        value={value}
        size="small"
        onChange={onChange}
        onInputChange={(_, value) => setSearchTerm(value)}
        renderInput={(params) => (
          <TextField
            {...params}
            id="townName"
            placeholder={t("start.townPlaceholder")}
            error={errors}
            helperText={
              errors?.required ? t("start.validations.requiredField") : ""
            }
            variant="outlined"
            fullWidth
          />
        )}
        renderOption={RenderTownOptions}
      />
    </>
  );
}

function RenderTownOptions(
  props: React.HTMLAttributes<HTMLLIElement>,
  option: PlaceType
) {
  const matches = option.structured_formatting.main_text_matched_substrings;
  const parts = parse(
    option.structured_formatting.main_text,
    matches.map((match: any) => [match.offset, match.offset + match.length])
  );

  return (
    <li {...props}>
      <Grid container alignItems="center">
        <Grid item>
          <Box
            component={LocationOnIcon}
            sx={{ color: "text.secondary", mr: 2 }}
          />
        </Grid>
        <Grid item xs>
          {parts.map((part, index) => (
            <Typography
              key={index}
              display="inline"
              fontWeight={part.highlight ? 700 : 400}
            >
              {part.text}
            </Typography>
          ))}
          <Typography variant="body2" color="text.secondary">
            {option.structured_formatting.secondary_text}
          </Typography>
        </Grid>
      </Grid>
    </li>
  );
}
